import { ArrowRight, Scale, Activity, ClipboardList, StickyNote } from 'lucide-react';
import type { PatientListItem } from '../lib/types';
import styles from './PatientDetailPanel.module.css';

interface PatientDetailPanelProps {
  patient: PatientListItem | null;
  onOpenPlan?: (patient: PatientListItem) => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatLastCheck(iso: string | null): string {
  if (!iso) return 'Nessun accesso';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

/** Trasparenza multi-piano (0035, Q2 design): badge origine del piano ATTIVO del paziente. */
function originBadge(origin: PatientListItem['current_plan_origin']): { label: string; cls: string } | null {
  switch (origin) {
    case 'own':
      return { label: 'Il tuo piano', cls: styles.originOwn };
    case 'self_pdf':
      return { label: 'PDF proprio', cls: styles.originSelfPdf };
    case 'other_pro':
      return { label: 'Altro studio', cls: styles.originOther };
    default:
      return null;
  }
}

export function PatientDetailPanel({ patient, onOpenPlan }: PatientDetailPanelProps) {
  if (!patient) {
    return (
      <aside className={styles.panel}>
        <div className={styles.empty}>
          <p>Seleziona un cliente per vedere il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const adh = patient.adherence_7d_pct;
  const adhCardClass = adh != null && adh >= 80 ? styles.cardAdhGood : '';

  return (
    <aside className={styles.panel}>
      <header className={styles.header}>
        <div className={styles.avatar}>{initials(patient.name)}</div>
        <div className={styles.headerInfo}>
          <h2 className={styles.name}>{patient.name}</h2>
          <div className={styles.meta}>
            {patient.human_id && <span className={styles.id}>{patient.human_id}</span>}
            {patient.last_check && (
              <>
                <span className={styles.sep}>·</span>
                <span className={styles.lastCheck}>
                  Ultimo accesso {formatLastCheck(patient.last_check)}
                </span>
              </>
            )}
          </div>
        </div>
        <button
          className={styles.openCta}
          type="button"
          onClick={() => onOpenPlan?.(patient)}
        >
          Apri piano
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </header>

      <div className={styles.cardsGrid}>
        <div className={styles.card}>
          <div className={styles.cardEyebrow}>
            <Scale size={13} strokeWidth={2.2} /> Peso
          </div>
          <div className={`${styles.cardValue} tnum`}>
            {patient.weight_kg != null ? `${patient.weight_kg.toFixed(1)}` : '—'}
            {patient.weight_kg != null && <span className={styles.cardUnit}>kg</span>}
          </div>
          {patient.weight_delta_kg != null && (
            <div className={`${styles.cardSub} tnum`}>
              {patient.weight_delta_kg > 0 ? '+' : ''}
              {patient.weight_delta_kg.toFixed(1)} kg · 30g
            </div>
          )}
        </div>

        <div className={`${styles.card} ${adhCardClass}`}>
          <div className={styles.cardEyebrow}>
            <Activity size={13} strokeWidth={2.2} /> Aderenza
          </div>
          <div className={`${styles.cardValue} tnum`}>
            {adh != null ? `${adh}` : '—'}
            {adh != null && <span className={styles.cardUnit}>%</span>}
          </div>
          <div className={styles.cardSub}>Ultima sett.</div>
        </div>
      </div>

      <div className={styles.cardWide}>
        <div className={styles.cardEyebrow}>
          <ClipboardList size={13} strokeWidth={2.2} /> Sta seguendo
        </div>
        <div className={styles.cardWideRow}>
          <div className={styles.cardWideText}>
            {patient.current_plan_label || 'Nessun piano attivo'}
          </div>
          {(() => {
            const badge = originBadge(patient.current_plan_origin);
            return badge ? (
              <span className={`${styles.originBadge} ${badge.cls}`}>{badge.label}</span>
            ) : null;
          })()}
        </div>
      </div>

      <div className={styles.cardWide}>
        <div className={styles.cardEyebrow}>
          <StickyNote size={13} strokeWidth={2.2} /> Note
        </div>
        <div className={styles.cardWideText}>
          {patient.mods_7d > 0
            ? `${patient.mods_7d} modifiche negli ultimi 7 giorni.`
            : 'Nessuna modifica recente.'}
        </div>
      </div>
    </aside>
  );
}
